import type { ReactNode } from "react";
import { Children, createContext, useMemo } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks } from "@builder.io/sdk-react";

import { Column, Row } from "@jti/core-components/components/Row";

const columnWidthMap = {
  "3": 3,
  "4": 4,
  "6": 6,
  "12": 12,
} as const;

type ColumnWidthKeys = keyof typeof columnWidthMap;
type ColumnWidthValues = (typeof columnWidthMap)[ColumnWidthKeys];

const parseColumnWidth = (value: ColumnWidthKeys): ColumnWidthValues => columnWidthMap[value];

export interface GridProps {
  children?: ReactNode;
  columnDesktopWidth: ColumnWidthKeys;
  columnTabletWidth?: ColumnWidthKeys;
  builderBlock?: BuilderBlock;
  builderContext?: BuilderContextInterface;
  builderComponents?: RegisteredComponents;
  attributes?: Record<string, string>;
}

export const GridContext = createContext<
  | {
      columnDesktopWidth: ColumnWidthKeys;
      columnTabletWidth?: ColumnWidthKeys;
    }
  | undefined
>(undefined);

export const Grid = ({
  children,
  columnDesktopWidth = "4",
  columnTabletWidth,
  builderBlock,
  builderContext,
  builderComponents,
  attributes,
}: GridProps) => {
  const isBuilderMode = !!builderBlock;

  const value = useMemo(() => ({ columnDesktopWidth, columnTabletWidth }), [columnDesktopWidth, columnTabletWidth]);

  return (
    <GridContext.Provider value={value}>
      <Row {...attributes}>
        {isBuilderMode
          ? (builderBlock.children ?? []).filter(Boolean).map((childBlock, index) => (
              <Column
                key={childBlock.id ?? index}
                sm={12}
                md={parseColumnWidth(columnTabletWidth || columnDesktopWidth)}
                lg={parseColumnWidth(columnDesktopWidth)}
              >
                <Blocks
                  blocks={[childBlock]}
                  parent={builderBlock.id}
                  path={`children.${index}`}
                  context={builderContext}
                  registeredComponents={builderComponents}
                />
              </Column>
            ))
          : Children.map(children, (child) => (
              <Column
                sm={12}
                md={parseColumnWidth(columnTabletWidth || columnDesktopWidth)}
                lg={parseColumnWidth(columnDesktopWidth)}
              >
                {child}
              </Column>
            ))}
      </Row>
    </GridContext.Provider>
  );
};
